/**
 * Image utilities for local asset handling
 * Replaces Base44 image dependencies with local images
 */

// Dynamic base URL determination
const getBaseURL = () => {
  // In production, always use the full domain
  if (window.location.hostname === 'spreewald-lift.de' || 
      window.location.hostname === 'www.spreewald-lift.de' ||
      window.location.origin.includes('spreewald-lift.de')) {
    return 'https://spreewald-lift.de';
  }
  
  // Local development - keep static images on the Vite dev server
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return window.location.origin;
  }
  
  // Default fallback
  return window.location.origin;
};

// Image paths configuration
const IMAGE_PATHS = {
  platforms: '/images/platforms',
  logos: '/images/logos', 
  hero: '/images/hero',
  content: '/images/assets',
  uploads: '/storage/images/uploads' // For uploaded files
};

/**
 * Get image URL with proper base URL
 * @param {string} imagePath - Relative image path
 * @returns {string} Full image URL
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // If it's already a full URL, return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // If it's a data URL, return as is
  if (imagePath.startsWith('data:')) {
    return imagePath;
  }
  
  // For uploaded files (starts with /storage/), use current domain
  if (imagePath.startsWith('/storage/')) {
    return getBaseURL() + imagePath;
  }
  
  // For other paths, use current domain
  if (imagePath.startsWith('/')) {
    return getBaseURL() + imagePath;
  }
  
  // Relative path, add base URL
  return getBaseURL() + '/' + imagePath;
};

/**
 * Get platform image URL
 * @param {string} imageName - Image name or full path
 * @returns {string} Platform image URL
 */
export const getPlatformImageUrl = (imageName) => {
  if (!imageName) return '/images/platforms/placeholder.svg';
  
  // If it's already a full URL or upload path, use getImageUrl
  if (imageName.startsWith('http') || imageName.startsWith('/storage/') || imageName.startsWith('/')) {
    return getImageUrl(imageName);
  }
  
  // Otherwise, it's a local platform image
  return getImageUrl(`${IMAGE_PATHS.platforms}/${imageName}`);
};

/**
 * Get content image by name
 * @param {string} name - Image name
 * @param {string} format - Image format
 * @returns {string} Local image path
 */
export const getContentImage = (name, format = 'jpg') => {
  return getImageUrl(`${IMAGE_PATHS.content}/${name}.${format}`);
};

/**
 * Get logo image URL
 * @param {string} logoName - Logo name or full path
 * @returns {string} Logo image URL
 */
export const getLogoUrl = (logoName) => {
  if (!logoName) return '/images/logos/spreewald-logo-white.svg';
  
  // If it's already a full URL or upload path, use getImageUrl
  if (logoName.startsWith('http') || logoName.startsWith('/storage/') || logoName.startsWith('/')) {
    return getImageUrl(logoName);
  }
  
  // Otherwise, it's a local logo
  return getImageUrl(`${IMAGE_PATHS.logos}/${logoName}`);
};

/**
 * Refresh image URL to force reload (bust cache)
 * @param {string} imageUrl - Original image URL
 * @returns {string} URL with cache-busting parameter
 */
export const refreshImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  
  const url = new URL(imageUrl, window.location.origin);
  url.searchParams.set('v', Date.now().toString());
  return url.toString();
};

/**
 * Force refresh all images on the page
 * Useful after uploading new images to avoid cache issues
 */
export const refreshAllImagesOnPage = () => {
  const images = document.querySelectorAll('img');
  images.forEach(img => {
    if (img.src && (img.src.includes('/storage/') || img.src.includes('/images/'))) {
      const originalSrc = img.src;
      const refreshedSrc = refreshImageUrl(originalSrc);
      img.src = refreshedSrc;
      console.log('🔄 Refreshed image:', originalSrc, '→', refreshedSrc);
    }
  });
};

// Export constants for use in other modules
export { IMAGE_PATHS, getBaseURL }; 
